import React from "react";
import { Helmet } from "react-helmet-async";
import { stripQueryAndHash, toAbsoluteUrl } from "./seoUtils";

type JsonLd = Record<string, unknown>;

export type SEOProps = {
  title: string;
  description?: string;
  canonicalPath?: string;
  ogType?: "website" | "article";
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
  noIndex?: boolean;
  jsonLd?: JsonLd | JsonLd[];
};

const DEFAULT_OG_IMAGE_PATH = "/logo512.png";

export default function SEO({
  title,
  description,
  canonicalPath,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE_PATH,
  ogImageWidth,
  ogImageHeight,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleTags,
  noIndex,
  jsonLd,
}: SEOProps) {
  const canonical =
    canonicalPath && canonicalPath.length > 0
      ? toAbsoluteUrl(stripQueryAndHash(canonicalPath))
      : typeof window !== "undefined"
      ? toAbsoluteUrl(stripQueryAndHash(window.location.pathname))
      : toAbsoluteUrl("/");

  const ogImageAbs = toAbsoluteUrl(ogImage);
  const scripts = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{title}</title>
      {description ? <meta name="description" content={description} /> : null}

      <link rel="canonical" href={canonical} />

      {noIndex ? (
        <>
          <meta name="robots" content="noindex,nofollow" />
          <meta name="googlebot" content="noindex,nofollow" />
        </>
      ) : null}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      {description ? (
        <meta property="og:description" content={description} />
      ) : null}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImageAbs} />
      {ogImageWidth ? (
        <meta property="og:image:width" content={String(ogImageWidth)} />
      ) : null}
      {ogImageHeight ? (
        <meta property="og:image:height" content={String(ogImageHeight)} />
      ) : null}
      <meta property="og:image:alt" content={title} />

      {/* Article-specific Open Graph tags */}
      {ogType === "article" && (
        <>
          {articlePublishedTime ? (
            <meta
              property="article:published_time"
              content={articlePublishedTime}
            />
          ) : null}
          {articleModifiedTime ? (
            <meta
              property="article:modified_time"
              content={articleModifiedTime}
            />
          ) : null}
          {articleAuthor ? (
            <meta property="article:author" content={articleAuthor} />
          ) : null}
          {articleSection ? (
            <meta property="article:section" content={articleSection} />
          ) : null}
          {articleTags && articleTags.length > 0
            ? articleTags.map((tag, idx) => (
                <meta key={idx} property="article:tag" content={tag} />
              ))
            : null}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description ? (
        <meta name="twitter:description" content={description} />
      ) : null}
      <meta name="twitter:image" content={ogImageAbs} />
      <meta name="twitter:image:alt" content={title} />

      {/* Structured data */}
      {scripts.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
